import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { QuizSource, QuizSourceInsert } from '@/types/quizSources';
import { toast } from 'sonner';

export const useQuizSources = (brandId?: string) => {
  return useQuery({
    queryKey: ['quiz-sources', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      
      const { data, error } = await supabase
        .from('os_quiz_sources')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as unknown as QuizSource[];
    },
    enabled: !!brandId,
  });
};

export const useQuizSource = (sourceId?: string) => {
  return useQuery({
    queryKey: ['quiz-source', sourceId],
    queryFn: async () => {
      if (!sourceId) return null;
      
      const { data, error } = await supabase
        .from('os_quiz_sources')
        .select('*')
        .eq('id', sourceId)
        .single();

      if (error) throw error;
      return data as unknown as QuizSource;
    },
    enabled: !!sourceId,
  });
};

export const useCreateQuizSource = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (sourceData: QuizSourceInsert) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('os_quiz_sources')
        .insert({
          ...sourceData,
          user_id: user.user.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Trigger generation via edge function
      const { error: functionError } = await supabase.functions.invoke('generate-quiz-from-source', {
        body: {
          sourceId: data.id,
          brandId: sourceData.brand_id,
        },
      });

      if (functionError) {
        console.error('Edge function error:', functionError);
        throw functionError;
      }

      return data as unknown as QuizSource;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quiz-sources', variables.brand_id] });
      toast.success('Quiz generation started!');
    },
    onError: (error) => {
      console.error('Error creating quiz source:', error);
      toast.error('Failed to start quiz generation');
    },
  });
};

export const useUpdateQuizSource = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, updates, brandId }: { id: string; updates: any; brandId: string }) => {
      const { data, error } = await supabase
        .from('os_quiz_sources')
        .update(updates as any)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data: data as unknown as QuizSource, brandId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['quiz-sources', result.brandId] });
      queryClient.invalidateQueries({ queryKey: ['quiz-source', result.data.id] });
      toast.success('Quiz source updated successfully');
    },
    onError: (error) => {
      console.error('Error updating quiz source:', error);
      toast.error('Failed to update quiz source');
    },
  });
};

export const useDeleteQuizSource = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, brandId }: { id: string; brandId: string }) => {
      const { error } = await supabase
        .from('os_quiz_sources')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return brandId;
    },
    onSuccess: (brandId) => {
      queryClient.invalidateQueries({ queryKey: ['quiz-sources', brandId] });
      toast.success('Quiz source deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting quiz source:', error);
      toast.error('Failed to delete quiz source');
    },
  });
};

export const useRegenerateQuizSource = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ sourceId, brandId }: { sourceId: string; brandId: string }) => {
      // Update status to processing
      const { error: updateError } = await supabase
        .from('os_quiz_sources')
        .update({ 
          generation_status: 'processing',
          generation_error: null,
        })
        .eq('id', sourceId);

      if (updateError) throw updateError;

      // Trigger generation via edge function
      const { error: functionError } = await supabase.functions.invoke('generate-quiz-from-source', {
        body: {
          sourceId,
          brandId,
        },
      });

      if (functionError) throw functionError;

      return { sourceId, brandId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['quiz-sources', result.brandId] });
      queryClient.invalidateQueries({ queryKey: ['quiz-source', result.sourceId] });
      toast.success('Quiz regeneration started!');
    },
    onError: (error) => {
      console.error('Error regenerating quiz source:', error);
      toast.error('Failed to regenerate quiz');
    },
  });
};
